import { useQuery } from '@apollo/client'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import type { ToggleIconProps } from '@/components_deprecated'
import { ModuleTitle, Toggle } from '@/components_deprecated'
import { addMonths, endOfMonth, setDate, startOfMonth } from 'date-fns'
import { Module } from '@/modules/companies'
import queryString from 'query-string'
import React, { useEffect, useEffectEvent, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useLocation } from 'react-router'
import styled from 'styled-components'
import { endOfWeek, startOfWeek } from '@/utils/date-fns'
import {
  useAdmin,
  useOnErrorAuto,
  usePermissions,
  useUser,
  useUserLeaderGroups,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { AbsencesCalendar } from './components/AbsencesCalendar'
import { AbsencesTable } from './components/AbsencesTable'
import { TimesheetControls } from './components/TimesheetControls'
import { AbsenceView } from './consts'
import { USER_ABSENCES_QUERY } from './queries'
import type { AbsenceFilterForm } from './types'
import type {
  AbsenceTypeNode,
  UserAbsencesQuery,
  UserAbsencesQueryVariables,
} from './types.graphql'
import { useInfoModal } from '@/utils/hooks/useInfoModal/useInfoModal'
import { PageType } from '@/modules/common/consts'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`

const TitleWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
`

interface AbsencesProps {}

interface InitialFilterParams {
  userId: string
  date: Date
}

export const Absences: React.FC<AbsencesProps> = () => {
  const translations = useTranslate({
    absences: 'common.absences',
  })
  const [selectedAbsenceType, setSelectedAbsenceType] = useState<Pick<
    AbsenceTypeNode,
    'id' | 'name'
  > | null>(null)
  const me = useUser()
  const isAdmin = useAdmin()
  const location = useLocation()
  const { hasPermissions } = usePermissions()

  const { InfoModal, infoContent, InfoButton } = useInfoModal(PageType.ABSENCES)

  const initialFilter: InitialFilterParams = useMemo(() => {
    const search = queryString.parse(location.search)
    const date = (search.date as string) || null
    const userId = (search.user as string) || null
    return {
      date: date ? new Date(date) : new Date(),
      userId: hasPermissions(PERMISSIONS.timesheets.change.absence)
        ? userId ?? me.id
        : me.id,
    }
  }, [hasPermissions, location.search, me.id])

  const [initialLoaded, setInitialLoaded] = useState(false)

  const { hasAccess, myLeaderGroups, groupsLoading, defaultLeaderGroup } =
    useUserLeaderGroups(me.id, isAdmin)

  const { formValues: filter, updateForm } = useForm<AbsenceFilterForm>({
    values: {
      users: [],
      user: undefined,
      month: initialFilter.date,
      date: [initialFilter.date, initialFilter.date],
      isDate: false,
      isDateStart: false,
      orderBy: 'start',
      allUsers: false,
      requestStatus: undefined,
      view: AbsenceView.table,
      salaryMode: false,
      leaderGroup: null,
      leaderGroupUsers: [],
    },
    config: {
      storage: {
        storeFormState: true,
        storeFormStateName: 'absence-controls',
        retrieveFormStateOnMount: true,
        transformValues: {
          month: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          salaryMode: value => (typeof value === 'boolean' ? value : false),
        },
      },
    },
  })

  const user = useUser(filter.user, Module.TIMESHEETS)

  const { datetimeStartLt, datetimeEndGte } = useMemo(() => {
    if (
      filter.salaryMode &&
      user.salaryWorkTime &&
      filter.view === AbsenceView.table
    ) {
      const salaryStart = user.salaryWorkTime?.salaryDateStart ?? 1
      const salaryEnd = user.salaryWorkTime?.salaryDateEnd ?? 1

      const forward = 31 - salaryStart > 15

      // What salary period is connected to which month is based on the salary start date.
      if (forward) {
        const datetimeStartLt = setDate(filter.month, salaryStart)
        const datetimeEndGte =
          salaryStart === 1
            ? endOfMonth(filter.month)
            : setDate(addMonths(filter.month, 1), salaryEnd)

        return { datetimeStartLt, datetimeEndGte }
      }

      const datetimeStartLt = setDate(addMonths(filter.month, -1), salaryStart)
      const datetimeEndGte = setDate(filter.month, salaryEnd)

      return { datetimeStartLt, datetimeEndGte }
    } else {
      const startOfMonthDate = startOfMonth(filter.month)
      const endOfMonthDate = addMonths(startOfMonthDate, 1)

      const datetimeStartLt =
        filter.view === AbsenceView.table
          ? startOfMonthDate
          : startOfWeek(startOfMonthDate)
      const datetimeEndGte =
        filter.view === AbsenceView.table
          ? endOfMonthDate
          : endOfWeek(endOfMonthDate)

      return {
        datetimeStartLt,
        datetimeEndGte,
      }
    }
  }, [filter.month, filter.salaryMode, filter.view, user.salaryWorkTime])

  const onErrorAuto = useOnErrorAuto()

  const getDateFilters = () => {
    if (filter.view === AbsenceView.table) {
      const base: Record<string, Date> = {}
      const start = new Date(
        Date.UTC(
          filter.date[0].getFullYear(),
          filter.date[0].getMonth(),
          filter.date[0].getDate(),
          0,
          0,
          0
        )
      )

      const end = new Date(
        Date.UTC(
          filter.date[1].getFullYear(),
          filter.date[1].getMonth(),
          filter.date[1].getDate(),
          23,
          59,
          59,
          999
        )
      )
      if (filter.isDate) {
        base.createdStart = start
        base.createdEnd = end
      }

      if (filter.isDateStart) {
        base.absenceStartDate = start
        base.absenceEndDate = end
      }

      return base
    }

    return {
      absenceStartDate: datetimeStartLt,
      absenceEndDate: datetimeEndGte,
    }
  }

  const { data, loading } = useQuery<
    UserAbsencesQuery,
    UserAbsencesQueryVariables
  >(USER_ABSENCES_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      user:
        !filter.allUsers && filter.view === AbsenceView.table
          ? filter.user
          : undefined,
      users: !filter.allUsers ? filter.users : undefined,
      absenceType: selectedAbsenceType?.id,
      requestStatus: filter.requestStatus,
      ...getDateFilters(),
    },
    onError: onErrorAuto(),
  })

  const absences = (
    data?.allAbsences.edges.map(edge => edge.node) ?? []
  ).filter(absence => {
    if (!filter.leaderGroupUsers.length) return true
    return filter.leaderGroupUsers.includes(absence.user.id)
  })

  const iconProps: Omit<ToggleIconProps, 'icon'> = {
    size: '1.6rem',
  }

  const handleSetInitialLoaded = useEffectEvent(() => {
    setInitialLoaded(true)
  })

  useEffect(() => {
    if (initialFilter.date !== filter.date[0] && !initialLoaded) {
      updateForm({ date: [initialFilter.date, initialFilter.date] })
      handleSetInitialLoaded()
    }
  }, [filter.date, initialFilter.date, initialLoaded, updateForm])

  // TODO: Add to multiple pages if logic is suitable
  useEffect(() => {
    if (
      defaultLeaderGroup &&
      !filter.leaderGroup &&
      myLeaderGroups.length === 1 &&
      !isAdmin
    ) {
      updateForm({ leaderGroup: defaultLeaderGroup.id })
    }
  }, [
    defaultLeaderGroup,
    filter.leaderGroup,
    isAdmin,
    myLeaderGroups.length,
    updateForm,
  ])

  return (
    <Wrapper>
      <TitleWrapper>
        <ModuleTitle>
          {translations.absences}
          {infoContent && <InfoButton />}
        </ModuleTitle>
        {!isMobile && (
          <div>
            <Toggle
              value={filter.view === AbsenceView.table}
              offIconProps={{
                icon: 'calendar-alt',
                ...iconProps,
                color: !(filter.view === AbsenceView.table)
                  ? 'white'
                  : undefined,
              }}
              onIconProps={{
                icon: 'list',
                ...iconProps,
                color: filter.view === AbsenceView.table ? 'white' : undefined,
              }}
              thumbBackground="secondary"
              background="white"
              height="3.875rem"
              onChange={value =>
                updateForm({
                  view: value ? AbsenceView.table : AbsenceView.calendar,
                })
              }
            />
          </div>
        )}
      </TitleWrapper>
      <TimesheetControls
        filter={filter}
        type="absences"
        onUpdate={updateForm}
        leaderGroupsInfo={{
          groupLoading: groupsLoading,
          leaderGroupFilter: hasAccess,
          leaderGroups: myLeaderGroups,
        }}
      />

      {filter.view === AbsenceView.table ? (
        <AbsencesTable
          absences={absences}
          loading={loading}
          showEmployee={filter.user !== me.id}
          selectedAbsenceType={selectedAbsenceType}
          onSelectAbsenceType={absenceType => {
            setSelectedAbsenceType(absenceType)
          }}
        />
      ) : (
        <AbsencesCalendar
          startDate={datetimeStartLt}
          endDate={datetimeEndGte}
          absences={absences}
        />
      )}
      {infoContent && <InfoModal />}
    </Wrapper>
  )
}
